import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import apiDefinitions from '../../../core/api/apiDefinitions';
import { checkPermission } from '../../../core/permissons/CheckPermissons';
import { showError } from '../../../core/alert/sweetAlertConfig';
import { all_routes } from '../../router/all_routes';
import CollapseHeader from '../../../core/common/collapse-header';

interface LetterDTO {
  id: number;
  date: string;
  referenceNo: string;
  letterType: string;
  senderDetails: string;
  letterDescription: string;
  status: string;
  handovers: any[];
}

interface User {
  empNumber: string;
  name: string;
}

interface Department {
  id: number;
  name: string;
}

interface GetLettersResponse {
  status: string;
  statusDescription: string;
  data: LetterDTO[];
  totalCount?: number;
}

const navItems = [
  { label: 'Department Letters', icon: 'ti ti-receipt', link: all_routes.departmentLetters, permission: 'ROLE_VIEW_LETTER' },
];

const ITEMS_PER_PAGE = 10;

const DepartmentLetters: React.FC = () => {
  const locationHook = useLocation();
  const [letters, setLetters] = useState<LetterDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);

  const checkToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showError('Authentication token not found. Please log in.');
      window.location.href = '/login';
      return false;
    }
    return token;
  };

  const fetchLetters = async (page: number) => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_VIEW_DEPARTMENT_LETTERS')) {
      showError('You do not have permission to view department letters.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const payload = { filterType: null, filterValue: '', date: null };
      const res: AxiosResponse = await apiDefinitions.getLettersForRelatedDepartments(page, payload);
      if (res.status === 200 && res.data.status === 'S1000') {
        const data: GetLettersResponse = res.data;
        setLetters(data.data || []);
        const totalCount = res.data.totalCount !== undefined ? res.data.totalCount : (data.data.length === ITEMS_PER_PAGE ? ITEMS_PER_PAGE * 2 : data.data.length);
        setTotalPages(Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE)));
        setHasMorePages(data.data.length === ITEMS_PER_PAGE);
        if ((data.data || []).length === 0 && page === 1) {
          setError('No department letters found.');
          setHasMorePages(false);
        } else if ((data.data || []).length === 0 && page > 1) {
          setCurrentPage((prev) => Math.max(1, prev - 1));
          setError('No more department letters to display.');
          setHasMorePages(false);
        }
      } else {
        showError(res.data?.statusDescription || 'Failed to fetch department letters');
      }
    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        showError('Failed to fetch department letters. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };




  useEffect(() => {
    fetchLetters(currentPage);
  }, [currentPage]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && (page <= totalPages || hasMorePages)) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Department Letters</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body pb-0 pt-2">
                <ul className="nav nav-tabs nav-tabs-bottom" style={{ gap: '12px', display: 'flex' }}>
                  {navItems.map((item, i) =>
                    item.permission && checkPermission(item.permission) ? (
                      <li className="nav-item me-item" key={i}>
                        <Link
                          to={item.link}
                          className={`nav-link px-0 ${locationHook.pathname === item.link ? 'active' : ''}`}
                        >
                          <i className={item.icon}></i> {item.label}
                        </Link>
                      </li>
                    ) : null
                  )}
                </ul>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                {isLoading ? (
                  <div className="text-center">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className="alert alert-danger" role="alert">{error}</div>
                ) : letters.length === 0 ? (
                  <div className="alert alert-info" role="alert">No department letters found.</div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Date</th>
                            <th>Reference No</th>
                            <th>Letter Type</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {letters.map((letter) => (
                            <tr key={letter.id}>
                              <td>{letter.id}</td>
                              <td>{new Date(letter.date).toLocaleDateString()}</td>
                              <td>{letter.referenceNo}</td>
                              <td>{letter.letterType}</td>
                              <td>
                                <span
                                  className={`badge ${
                                    letter.status === 'RECEIVED' ? 'bg-success' : 'bg-info'
                                  }`}
                                >
                                  {letter.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <button
                        className="btn btn-light"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        Previous
                      </button>
                      <span>
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        className="btn btn-light"
                        disabled={!hasMorePages && currentPage >= totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        Next
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepartmentLetters;
